import { X } from "lucide-react";
import React from "react";

interface ItemDetailsModalProps {
  item: any;
  onClose: () => void;
  open: boolean;
}

const getImageUrl = (imgPath: File | string) => {
  if (!imgPath) return "";
  if (imgPath instanceof File) return URL.createObjectURL(imgPath);
  if (typeof imgPath === "string") {
    const BASE_URL = import.meta.env.VITE_API_STORAGE_URL || "";
    if (imgPath.startsWith("http")) return imgPath;
    return (
      BASE_URL.replace(/\/$/, "") + "/storage/" + imgPath.replace(/^\//, "")
    );
  }
  return "";
};

const ItemDetailsModal: React.FC<ItemDetailsModalProps> = ({
  item,
  onClose,
  open,
}) => {
  if (!open || !item) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-2 sm:p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative w-full max-w-2xl rounded-xl bg-white shadow-2xl dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label="Close"
          className="absolute right-4 top-4 rounded-full p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          onClick={onClose}
        >
          <X className="h-6 w-6" />
        </button>
        <div className="flex flex-col gap-6 p-6 md:flex-row">
          <div className="flex flex-shrink-0 items-center justify-center md:w-1/2">
            <img
              alt={item.name}
              className="h-48 w-full rounded-lg bg-gray-100 object-contain dark:bg-gray-800"
              src={getImageUrl(item.images?.[0] || item.image)}
            />
          </div>
          <div className="flex flex-col gap-2 md:w-1/2">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {item.name}
            </h2>
            <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
              R{item.price}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {item.category}
            </p>
            <p className="mt-2 text-gray-700 dark:text-gray-200">
              {item.description}
            </p>
            {item.variants && item.variants.length > 0 && (
              <div className="mt-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  Variants
                </h3>
                <ul className="list-disc pl-5 text-gray-700 dark:text-gray-200">
                  {item.variants.map((v: any, i: number) => (
                    <li key={i}>
                      {v.name} — R{v.price} ({v.stock} in stock)
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {item.addons && item.addons.length > 0 && (
              <div className="mt-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  Add-ons
                </h3>
                <ul className="list-disc pl-5 text-gray-700 dark:text-gray-200">
                  {item.addons.map((a: any, i: number) => (
                    <li key={i}>
                      {a.name} — R{a.price}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              {item.isAvailable ? (
                <span className="rounded bg-green-100 px-3 py-1 text-xs font-semibold text-green-800 dark:bg-green-900/20 dark:text-green-400">
                  Available
                </span>
              ) : (
                <span className="rounded bg-red-100 px-3 py-1 text-xs font-semibold text-red-800 dark:bg-red-900/20 dark:text-red-400">
                  Unavailable
                </span>
              )}
              {item.featured && (
                <span className="rounded bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                  Featured
                </span>
              )}
              {item.popular && (
                <span className="rounded bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                  Popular
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemDetailsModal;
